// src/components/RoadmapTimeline.jsx - COMPACT VERSION
import React, { useState, useMemo } from 'react';
import { Zap, Flag } from 'lucide-react';

/**
 * RoadmapTimeline - Compact visual horizontal timeline for implementation roadmap
 * Shows milestones, ROI inflection points, and project duration
 * 
 * Props:
 * - milestones: Array of milestone objects with { label, months_from_start }
 * - valueInflections: Array of ROI markers with { month, title, value, description }
 * - timeframeMonths: Total project duration in months
 * - currentProjectMonth: Current month in timeline (optional, for progress indicator)
 */
export default function RoadmapTimeline({ 
  milestones = [], 
  valueInflections = [],
  timeframeMonths = 24,
  currentProjectMonth = 0
}) {
  const [hoveredMilestone, setHoveredMilestone] = useState(null);
  const [hoveredInflection, setHoveredInflection] = useState(null);

  // Generate month markers for display (M1, M3, M6, M9, M12, M18, M24...)
  const monthMarkers = useMemo(() => {
    const markers = [1]; // Always show M1
    
    // Add markers every 3 months up to 12
    for (let i = 3; i <= 12; i += 3) {
      if (i <= timeframeMonths) markers.push(i);
    }
    
    // Add markers every 6 months after 12
    for (let i = 18; i <= timeframeMonths; i += 6) {
      markers.push(i);
    }
    
    // Always include the final month if not already there
    if (!markers.includes(timeframeMonths)) {
      markers.push(timeframeMonths);
    }
    
    return markers;
  }, [timeframeMonths]);

  // Calculate position percentage for a given month
  const getPositionPercent = (month) => {
    return (month / timeframeMonths) * 100;
  };

  // Get color for ROI inflection point
  const getInflectionColor = (type) => {
    if (type.toLowerCase().includes('first')) return {
      bg: 'bg-orange-100',
      border: 'border-orange-400',
      text: 'text-orange-700',
      icon: 'text-[#FF6E4C]',
      dot: 'bg-[#FF6E4C]'
    };
    if (type.toLowerCase().includes('break')) return {
      bg: 'bg-yellow-100',
      border: 'border-yellow-400',
      text: 'text-yellow-700',
      icon: 'text-[#FFB547]',
      dot: 'bg-[#FFB547]'
    };
    if (type.toLowerCase().includes('full') || type.toLowerCase().includes('roi')) return {
      bg: 'bg-green-100',
      border: 'border-green-400',
      text: 'text-green-700',
      icon: 'text-[#36C37D]',
      dot: 'bg-[#36C37D]'
    };
    return {
      bg: 'bg-blue-100',
      border: 'border-blue-400',
      text: 'text-blue-700',
      icon: 'text-blue-600',
      dot: 'bg-blue-600'
    };
  };

  return (
    <div className="w-full space-y-1">
      {/* ROI Inflection Points - Above Timeline - Reduced height */}
      {valueInflections.length > 0 && (
        <div className="relative h-14 mb-1">
          {valueInflections.map((inflection, idx) => {
            const position = getPositionPercent(inflection.month);
            const colors = getInflectionColor(inflection.title);
            const isHovered = hoveredInflection === idx;

            return (
              <div
                key={idx}
                className="absolute transform -translate-x-1/2"
                style={{ left: `${position}%`, top: 0 }}
                onMouseEnter={() => setHoveredInflection(idx)}
                onMouseLeave={() => setHoveredInflection(null)}
              >
                {/* Vertical line connecting to timeline */}
                <div className={`absolute top-11 left-1/2 transform -translate-x-1/2 w-0.5 h-5 ${colors.dot}`} />
                
                {/* ROI Marker Badge - More compact */}
                <div className={`
                  flex flex-col items-center gap-0.5 px-2 py-1 rounded-md border-2 transition-all duration-200
                  ${colors.bg} ${colors.border}
                  ${isHovered ? 'shadow-lg scale-105 z-10' : 'shadow-sm'}
                `}>
                  <div className="flex items-center gap-1">
                    <Zap className={`w-3 h-3 ${colors.icon}`} />
                    <span className={`text-xs font-bold ${colors.text} whitespace-nowrap`}>
                      {inflection.title}
                    </span>
                  </div>
                  <div className="text-xs font-semibold text-gray-900">
                    {inflection.value}
                  </div>
                  <div className="text-xs text-gray-600">M{inflection.month}</div>
                  
                  {/* Tooltip on hover */}
                  {isHovered && inflection.description && (
                    <div className="absolute top-full mt-2 w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-20">
                      {inflection.description}
                      <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Main Timeline Bar - Reduced spacing */}
      <div className="relative pt-1">
        {/* Timeline base bar */}
        <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
          {/* Progress indicator (if currentProjectMonth provided) */}
          {currentProjectMonth > 0 && (
            <div 
              className="absolute h-full bg-[#18365E] transition-all duration-500 rounded-full"
              style={{ width: `${getPositionPercent(currentProjectMonth)}%` }}
            />
          )}
        </div>

        {/* Month markers - Reduced spacing */}
        <div className="relative mt-1">
          {monthMarkers.map((month) => {
            const position = getPositionPercent(month);
            const isCurrent = currentProjectMonth === month;
            
            return (
              <div
                key={month}
                className="absolute transform -translate-x-1/2"
                style={{ left: `${position}%` }}
              >
                {/* Tick mark */}
                <div className={`absolute -top-4 left-1/2 transform -translate-x-1/2 w-0.5 h-3 ${
                  isCurrent ? 'bg-blue-600' : 'bg-gray-400'
                }`} />
                
                {/* Month label */}
                <div className={`text-xs font-medium mt-0.5 ${
                  isCurrent ? 'text-blue-600 font-bold' : 'text-gray-600'
                }`}>
                  M{month}
                </div>
              </div>
            );
          })}
        </div>

        {/* Milestone markers - Reduced spacing */}
        <div className="relative mt-6">
          {milestones.map((milestone, idx) => {
            const position = getPositionPercent(milestone.months_from_start);
            const isHovered = hoveredMilestone === idx;

            return (
              <div
                key={idx}
                className="absolute transform -translate-x-1/2 cursor-pointer"
                style={{ left: `${position}%` }}
                onMouseEnter={() => setHoveredMilestone(idx)}
                onMouseLeave={() => setHoveredMilestone(null)}
              >
                {/* Connecting line to timeline */}
                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 w-0.5 h-6 bg-[#18365E]" />
                
                {/* Milestone flag/dot - Smaller */}
                <div className={`
                  relative flex items-center justify-center w-7 h-7 rounded-full 
                  bg-[#18365E] border-2 border-white shadow-md transition-all duration-200
                  ${isHovered ? 'scale-125 shadow-lg z-10' : ''}
                `}>
                  <Flag className="w-3.5 h-3.5 text-white" />
                </div>

                {/* Milestone label - appears on hover */}
                {isHovered && (
                  <div className="absolute top-9 left-1/2 transform -translate-x-1/2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl z-20">
                    <div className="font-bold mb-1">Month {milestone.months_from_start}</div>
                    <div className="leading-relaxed">{milestone.label}</div>
                    {milestone.value_note && (
                      <div className="mt-2 text-gray-300 text-xs italic">
                        {milestone.value_note}
                      </div>
                    )}
                    {/* Tooltip arrow */}
                    <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend - More compact */}
      <div className="flex items-center gap-4 pt-1.5 border-t border-gray-200 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-[#18365E]" />
          <span className="text-gray-600">Milestone</span>
        </div>
        {valueInflections.length > 0 && (
          <>
            <div className="flex items-center gap-1.5">
              <Zap className="w-3 h-3 text-[#FF6E4C]" />
              <span className="text-gray-600">First Value</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Zap className="w-3 h-3 text-[#FFB547]" />
              <span className="text-gray-600">Break-Even</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Zap className="w-3 h-3 text-[#36C37D]" />
              <span className="text-gray-600">Full ROI</span>
            </div>
          </>
        )}
        {currentProjectMonth > 0 && (
          <div className="flex items-center gap-1.5 ml-auto">
            <div className="w-10 h-1.5 bg-[#18365E] rounded" />
            <span className="text-gray-600">Current Progress</span>
          </div>
        )}
      </div>
    </div>
  );
}